/**
 * PitchPrint Card Variants - ORIGINAL WORKING VERSION
 * Reverted to stable version without theme compatibility issues
 * Removes problematic theme integration that causes fetchAvailability errors
 */
(() => {
  'use strict';

  let initialized = false;
  let userHasInteracted = false;
  const stateManager = window.PitchPrintStateManager;
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[CardVariants]', ...args);

  if (!stateManager) {
    console.error('[CardVariants] StateManager not found - using fallback mode');
  }

  // =====================================
  // SAFE VARIANT FUNCTIONS - NO THEME CONFLICTS
  // =====================================
  
  const updateCardVisuals = (optionName, selectedValue) => {
    const group = document.querySelector(`[data-option-name="${optionName}"] .variants-cards`);
    if (!group) return;
    
    // Remove active from all cards in this group
    group.querySelectorAll('.card-p-style').forEach(card => {
      card.classList.remove('active');
      if (card.dataset.value === selectedValue) {
        card.classList.add('active');
      }
    });
  };
  
  const updateHiddenSelect = (optionName, selectedValue) => {
    const select = document.querySelector(`select[name="options[${optionName}]"]`);
    if (select && select.value !== selectedValue) {
      select.value = selectedValue;
      
      // Dispatch change event safely without triggering theme conflicts
      try {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        log('⚠️ Select change event error (non-critical):', error.message);
        // Continue execution even if theme has issues
      }
    }
  };
  
  const updateVariantInput = (variantId) => {
    const input = document.querySelector('input[name="id"]');
    if (input && input.value !== variantId) {
      input.value = variantId;
      
      // Safely dispatch events
      try {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        log('⚠️ Input event error (non-critical):', error.message);
      }
    }
  };
  
  const updateURL = (variantId) => {
    try {
      if (history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variantId);
        history.replaceState(null, '', url);
      }
    } catch (error) {
      log('⚠️ URL update error (non-critical):', error.message);
    }
  };

  // =====================================
  // SAFE VARIANT APPLICATION - NO THEME CONFLICTS
  // =====================================
  
  const applyVariant = (variantId, optionsArray) => {
    // If no variantId but we have options, try to find the matching variant
    if (!variantId && optionsArray && optionsArray.length > 0) {
      log('🔍 No variant ID provided, searching for variant matching options:', optionsArray);

      // Get all selects and their current values
      const selects = document.querySelectorAll('select[name^="options"]');

      // Apply the options to the selects
      selects.forEach((select, idx) => {
        if (optionsArray[idx]) {
          select.value = optionsArray[idx];
        }
      });

      // Trigger change on the last select to update the variant ID
      if (selects.length > 0) {
        const lastSelect = selects[selects.length - 1];
        lastSelect.dispatchEvent(new Event('change', { bubbles: true }));

        // Give the variant change event time to process
        setTimeout(() => {
          // Now get the current variant ID from the form
          const variantInput = document.querySelector('input[name="id"]');
          const foundVariantId = variantInput ? variantInput.value : null;

          if (foundVariantId) {
            log('✅ Found variant ID from options:', foundVariantId);
            // Update visuals for all options
            optionsArray.forEach((value, idx) => {
              const optionNameMatch = selects[idx].name.match(/\[(.*?)\]/);
              const optionName = optionNameMatch ? optionNameMatch[1] : null;
              if (optionName) {
                updateCardVisuals(optionName, value);
                log('🎨 Updated card visuals for:', optionName, '→', value);
              }
            });
          } else {
            log('⚠️ Could not find variant ID matching options');
          }
        }, 100);
      }

      return;
    }

    if (!variantId) {
      log('⚠️ No variant ID or options provided to applyVariant');
      return;
    }

    log('🔄 Applying variant:', variantId, 'with options:', optionsArray);

    try {
      // Update all hidden selects with the option values
      if (optionsArray) {
        document.querySelectorAll('select[name^="options"]').forEach((select, idx) => {
          const value = optionsArray[idx];
          if (value) {
            // Always update select value
            const valueChanged = select.value !== value;
            select.value = value;

            // ALWAYS update card visuals, even if select value didn't change
            // This ensures visual sync when URL parameter sets the variant
            const optionNameMatch = select.name.match(/\[(.*?)\]/);
            const optionName = optionNameMatch ? optionNameMatch[1] : null;
            if (optionName) {
              updateCardVisuals(optionName, value);
              log('🎨 Updated card visuals for:', optionName, '→', value);
            }

            // Only dispatch change event if value actually changed
            if (valueChanged) {
              try {
                select.dispatchEvent(new Event('change', { bubbles: true }));
              } catch (e) {
                // Continue if theme has compatibility issues
                log('⚠️ Theme compatibility issue (non-critical):', e.message);
              }
            }
          }
        });
      }

      // Update variant ID and URL
      updateVariantInput(variantId);
      updateURL(variantId);

      log('✅ Variant applied successfully:', variantId);

    } catch (error) {
      log('❌ Error applying variant:', error);
    }
  };

  // =====================================
  // POPULAR VARIANTS - Use metafield data
  // =====================================

  const applyPopularVariants = () => {
    log('🎯 Applying popular variants from metafield');

    try {
      // Find the popular variant element injected by liquid template
      const popularElement = document.querySelector('[data-popular-variant]');
      if (popularElement) {
        const popularVariantId = popularElement.dataset.popularVariant;
        const popularOptionsJson = popularElement.dataset.popularOptions;

        if (popularVariantId && popularOptionsJson) {
          try {
            const popularOptions = JSON.parse(popularOptionsJson);
            log('📋 Popular variant data from metafield:', { id: popularVariantId, options: popularOptions });
            applyVariant(popularVariantId, popularOptions);

            // v4.4.2: Dispatch event to notify other systems that popular variant was applied
            // This ensures design-radio-selector updates even if select value didn't change
            setTimeout(() => {
              document.dispatchEvent(new CustomEvent('popular-variant-applied', {
                detail: { variantId: popularVariantId, options: popularOptions }
              }));
              log('📡 Dispatched popular-variant-applied event');
            }, 50);

            return;
          } catch (error) {
            log('❌ Error parsing popular options:', error);
          }
        }
      }

      // Fallback: use first active card's variant
      const firstActiveCard = document.querySelector('.card-p-style.active[data-variant-id]');
      if (firstActiveCard) {
        const variantId = firstActiveCard.dataset.variantId;
        updateVariantInput(variantId);
        updateURL(variantId);
        log('✅ Applied fallback active variant:', variantId);

        // v4.4.2: Also dispatch event for fallback variant
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('popular-variant-applied', {
            detail: { variantId: variantId, source: 'fallback' }
          }));
          log('📡 Dispatched popular-variant-applied event (fallback)');
        }, 50);
      }

    } catch (error) {
      log('❌ Error applying popular variants:', error);
    }
  };

  // =====================================
  // SMART URL VARIANT MERGE - v4.4.4
  // Detect EX Show Variants shape-only selections and merge with popular Sides
  // =====================================

  /**
   * Get popular variant data from the hidden DOM element
   * @returns {object|null} Object with {id, options} or null if not found
   */
  const getPopularVariantData = () => {
    const el = document.querySelector('[data-popular-variant]');
    if (!el) return null;
    try {
      return {
        id: el.dataset.popularVariant,
        options: JSON.parse(el.dataset.popularOptions)
      };
    } catch (e) {
      log('⚠️ Error parsing popular variant data:', e);
      return null;
    }
  };

  /**
   * Get option names from option-group elements
   * @returns {string[]} Array of option names like ["Sides", "Shape"]
   */
  const getOptionNames = () => {
    return Array.from(document.querySelectorAll('.option-group[data-option-name]'))
      .map(group => group.dataset.optionName);
  };

  /**
   * Check if the user navigated from a collection page
   * @returns {boolean} true if referrer is a collection page
   */
  const isFromCollectionPage = () => {
    try {
      const referrer = document.referrer;
      if (!referrer) return false;

      // Check if referrer contains /collections/ path
      const isCollection = referrer.includes('/collections/');
      log('🔍 Referrer check:', { referrer, isCollection });
      return isCollection;
    } catch (e) {
      return false;
    }
  };

  /**
   * Determine if URL variant should be merged with popular variant's Sides option.
   *
   * v4.4.4: Enhanced to check referrer - if coming from a collection page,
   * always merge Sides regardless of shape (EX Show Variants scenario).
   *
   * Logic:
   * - If Sides match → no merge needed
   * - If from collection page → always merge (EX Show Variants click)
   * - If from elsewhere and shapes differ → merge (likely EX Show Variants)
   * - If from elsewhere and shapes match → user chose Sides intentionally, no merge
   *
   * @param {string[]} urlOptions - Options from URL variant
   * @param {string[]} popularOptions - Options from popular variant
   * @param {string[]} optionNames - Names of options
   * @returns {string[]|null} Merged options or null if no merge needed
   */
  const mergeUrlVariantWithPopular = (urlOptions, popularOptions, optionNames) => {
    if (!urlOptions || !popularOptions || !optionNames) return null;
    if (urlOptions.length !== popularOptions.length) return null;

    // Find Sides and Shape option indices
    const sidesIndex = optionNames.findIndex(name =>
      name.toLowerCase().includes('side')
    );
    const shapeIndex = optionNames.findIndex(name =>
      name.toLowerCase().includes('shape')
    );

    // Must have both Sides and Shape options for merge logic
    if (sidesIndex === -1 || shapeIndex === -1) {
      log('ℹ️ mergeUrlVariantWithPopular: Product does not have Sides and Shape options');
      return null;
    }

    const urlSides = urlOptions[sidesIndex];
    const popularSides = popularOptions[sidesIndex];
    const urlShape = urlOptions[shapeIndex];
    const popularShape = popularOptions[shapeIndex];

    log('🔀 mergeUrlVariantWithPopular: Comparing variants');
    log('   URL:', { sides: urlSides, shape: urlShape });
    log('   Popular:', { sides: popularSides, shape: popularShape });

    // If Sides match, no merge needed
    if (urlSides === popularSides) {
      log('ℹ️ Sides already match, no merge needed');
      return null;
    }

    // v4.4.4: Check if user came from a collection page
    // If so, they clicked a shape listing (EX Show Variants) - always merge Sides
    const fromCollection = isFromCollectionPage();

    if (fromCollection) {
      log('✨ From collection page - merging URL shape with popular sides');
    } else if (urlShape === popularShape) {
      // Not from collection and same shape - user likely chose Sides intentionally
      log('ℹ️ Same shape, different sides, not from collection - no merge');
      return null;
    } else {
      // Different shape, not from collection - could be shared URL, still merge
      log('✨ Different shapes detected - merging URL shape with popular sides');
    }

    const mergedOptions = [...urlOptions];
    mergedOptions[sidesIndex] = popularSides;

    return mergedOptions;
  };

  // =====================================
  // URL VARIANT CHECK - Respect URL parameter over popular variant
  // =====================================

  /**
   * Check for URL variant parameter and apply it if present.
   * Used to respect variant links from collection pages or external sources.
   * @returns {boolean} true if URL variant was applied, false otherwise
   */
  /**
   * Check for URL variant parameter and apply it if present.
   * v4.4.3: Enhanced to detect EX Show Variants shape-only selections
   * and merge with popular variant's Sides option.
   * @returns {boolean} true if URL variant was applied, false otherwise
   */
  const applyUrlVariantIfPresent = () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');

      if (!urlVariantId) {
        log('ℹ️ No variant parameter in URL');
        return false;
      }

      log('🔗 Found variant in URL parameter:', urlVariantId);

      // Get the current options from selects (Shopify server-renders the correct variant)
      const urlOptions = Array.from(document.querySelectorAll('select[name^="options"]'))
        .map(select => select.value)
        .filter(value => value && value !== '');

      if (urlOptions.length === 0) {
        // Fallback: just update the variant input (cards may not sync perfectly)
        log('⚠️ Could not get options for URL variant, applying ID only');
        updateVariantInput(urlVariantId);
        return true;
      }

      // v4.4.3: Check if this is an EX Show Variants shape-only selection
      // If so, merge URL's shape with popular variant's Sides
      const popularData = getPopularVariantData();
      const optionNames = getOptionNames();
      let finalOptions = urlOptions;
      let mergeApplied = false;

      if (popularData && optionNames.length > 0) {
        const mergedOptions = mergeUrlVariantWithPopular(urlOptions, popularData.options, optionNames);

        if (mergedOptions) {
          log('🔀 Using merged options (URL shape + popular sides)');
          finalOptions = mergedOptions;
          mergeApplied = true;
        } else {
          log('ℹ️ No merge needed, using URL variant as-is');
        }
      }

      // Apply the variant
      if (mergeApplied) {
        // Merged options - find variant by options (applyVariant handles null variantId)
        applyVariant(null, finalOptions);
        log('✅ Applied merged variant with options:', finalOptions);
      } else {
        // No merge - apply URL variant directly
        applyVariant(urlVariantId, finalOptions);
        log('✅ Applied URL variant:', urlVariantId, 'with options:', finalOptions);
      }

      // Dispatch events after a short delay to allow DOM updates
      setTimeout(() => {
        // Get the current variant ID (may differ from URL if merged)
        const currentVariantId = document.querySelector('input[name="id"]')?.value || urlVariantId;
        const source = mergeApplied ? 'url-parameter-merged' : 'url-parameter';

        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant: { id: currentVariantId }, source: source }
        }));
        log('📡 Dispatched variant:change event (' + source + ')');

        // v4.4.2: Also dispatch popular-variant-applied for design-radio-selector
        document.dispatchEvent(new CustomEvent('popular-variant-applied', {
          detail: { variantId: currentVariantId, options: finalOptions, source: source }
        }));
        log('📡 Dispatched popular-variant-applied event (' + source + ')');
      }, 150);

      return true;

    } catch (error) {
      log('❌ Error applying URL variant:', error);
      return false;
    }
  };

  // =====================================
  // SAFE CLICK HANDLER - NO THEME CONFLICTS
  // =====================================
  
  const setupVariantControls = () => {
    const container = document.querySelector('.custom-tab-body');
    if (!container) {
      log('❌ Custom tab body not found');
      return;
    }

    container.addEventListener('click', (e) => {
      const card = e.target.closest('.card-p-style');
      if (!card) return;

      // Check if card is conditionally hidden
      if (card.classList.contains('conditionally-hidden')) {
        log('🙈 Click ignored - card is conditionally hidden');
        return;
      }

      // Check if controls are locked by PitchPrint
      if (card.style.pointerEvents === 'none') {
        log('🔒 Click ignored - variant controls are locked');
        return;
      }

      // Check if card is already active
      if (card.classList.contains('active')) {
        log('ℹ️ Card already active - ignoring click:', card.dataset.value);
        return;
      }

      e.preventDefault();
      
      const optionName = card.dataset.optionName;
      const selectedValue = card.dataset.value;

      log('🎯 Card clicked:', { optionName, selectedValue });
      userHasInteracted = true;

      try {
        // 1. Update card visuals
        updateCardVisuals(optionName, selectedValue);
        
        // 2. Update hidden select safely
        updateHiddenSelect(optionName, selectedValue);
        
        // 3. Wait for Shopify's variant calculation, then update URL
        setTimeout(() => {
          const calculatedVariantId = document.querySelector('input[name="id"]')?.value;
          if (calculatedVariantId) {
            updateURL(calculatedVariantId);
            log('✅ Variant calculated by Shopify:', calculatedVariantId);
          }
        }, 100);

        // 4. Update PitchPrint observables safely
        updatePitchPrintObservables();

        // 5. Check if the new variant is hardware-only (no customization required)
        setTimeout(() => {
          checkAndUpdateHardwareOnlyState();
        }, 150); // After Shopify calculates the variant

        // 6. Only save variant state if there's an active project
        if (stateManager && stateManager.getProjectId()) {
          log('ℹ️ Variant change with active project - will update state');
        } else {
          log('ℹ️ Variant change without project - no state saving needed');
        }

      } catch (error) {
        log('❌ Error handling card click:', error);
      }
    });

    log('🎯 Safe variant click handlers attached');
  };

  // =====================================
  // PROJECT RESTORATION - Simplified & Safe
  // =====================================

  const handleProjectRestoration = () => {
    log('🎨 Checking for project restoration...');

    try {
      // Check if there's an active PitchPrint project
      const activeProjectId = getActiveProjectId();

      // NEW: Also check for pp_project URL parameter (My Designs duplication flow)
      const urlParams = new URLSearchParams(window.location.search);
      const urlProjectId = urlParams.get('pp_project');

      if (activeProjectId || urlProjectId) {
        const projectId = activeProjectId || urlProjectId;
        log('🔄 Found project for restoration:', projectId, urlProjectId ? '(from URL parameter)' : '(from StateManager/ppclient)');

        // Priority-based variant restoration with fallback chain
        let variantRestored = false;

        // PRIORITY 1: Check PitchPrint project storage (shopifyVariant field) - HIGHEST PRIORITY
        // This is the most reliable source as it's stored WITH the project
        const pitchprintVariantData = getProjectDataFromPitchPrintStorage(projectId);
        if (pitchprintVariantData && (pitchprintVariantData.variantId || pitchprintVariantData.options)) {
          log('💾 Found variant in PitchPrint storage (PRIORITY 1):', pitchprintVariantData);

          // If we have options but no variantId, applyVariant will find the matching variant
          // If we have variantId but no options, it will use the variantId directly
          applyVariant(pitchprintVariantData.variantId, pitchprintVariantData.options);
          variantRestored = true;
          lockControls();
        }

        // PRIORITY 2: Check localStorage pp_variant_${productId} (existing system)
        if (!variantRestored) {
          const savedVariantData = getSavedVariantData();
          if (savedVariantData && savedVariantData.projectId === projectId) {
            log('📂 Found variant in localStorage (PRIORITY 2):', savedVariantData);
            applyVariant(savedVariantData.variantId, savedVariantData.options);
            variantRestored = true;
            lockControls();
          }
        }

        // PRIORITY 3: Check URL parameters (?variant=X) - FALLBACK ONLY
        // URL variant is just what page we're on, not necessarily the correct variant for this project
        if (!variantRestored) {
          const urlParams = new URLSearchParams(window.location.search);
          const urlVariantId = urlParams.get('variant');

          if (urlVariantId) {
            log('🔗 Found variant in URL parameter (PRIORITY 3 - fallback):', urlVariantId);

            // Get the options for this variant by reading current select values
            const currentOptions = Array.from(document.querySelectorAll('select[name^="options"]'))
              .map(select => select.value)
              .filter(value => value && value !== '');

            // Apply variant with options to ensure visual cards update
            applyVariant(urlVariantId, currentOptions);
            variantRestored = true;

            // Give PitchPrint time to initialize before locking
            setTimeout(() => {
              lockControls();
              log('🔒 Locked controls after URL variant restoration');
            }, 1000);
          }
        }

        // FALLBACK: If no variant data found, lock current state
        if (!variantRestored) {
          log('⚠️ No variant data found - locking current state');
          lockControls();
        }

        // Ensure PitchPrint is in active state
        ensurePitchPrintActiveState(projectId);

        // Notify StateManager with COMPLETE project metadata
        if (stateManager) {
          const projectData = getProjectDataFromPitchPrintStorage(projectId);
          const currentVariant = getCurrentVariantData();

          // Extract metadata from project and variant
          const numPages = projectData?.numPages || projectData?.pageLength || (currentVariant.options[0] === 'Single Sided' ? 1 : 2);
          const shape = currentVariant.options[1] || null; // "Circle Shape" or "Classic Shape"
          const sides = currentVariant.options[0] || null; // "Single Sided" or "Double Sided"
          const size = currentVariant.options[2] || null; // Size option

          stateManager.setProject({
            projectId: projectId,
            variant: currentVariant,
            numPages: numPages,
            shape: shape,
            sides: sides,
            size: size
          });

          log('📡 StateManager notified with complete project data:', {
            projectId,
            variantId: currentVariant.id,
            numPages,
            shape,
            sides
          });
        }
      } else {
        // No active project - check URL variant first, then fall back to popular variants
        if (userHasInteracted) {
          log('👤 User already changed variant — skipping popular variant');
        } else {
          const urlVariantApplied = applyUrlVariantIfPresent();

          if (!urlVariantApplied) {
            log('🎯 No URL variant - applying popular variants');
            applyPopularVariants();
          }
        }

        unlockControls();
      }

      // Check if the current/initial variant is hardware-only
      setTimeout(() => {
        checkAndUpdateHardwareOnlyState();
      }, 200);

    } catch (error) {
      log('❌ Error during project restoration:', error);
      // Continue with URL variant or popular variants as fallback
      if (!userHasInteracted) {
        const urlVariantApplied = applyUrlVariantIfPresent();
        if (!urlVariantApplied) {
          applyPopularVariants();
        }
      }
      unlockControls();

      // Still check hardware-only state on error
      setTimeout(() => {
        checkAndUpdateHardwareOnlyState();
      }, 200);
    }
  };
  // =====================================
  // PITCHPRINT INTEGRATION - Safe
  // =====================================
  
  const handleProjectSaved = async (data) => {
    log('🎉 Project saved:', data);

    try {
      const projectId = data.data?.projectId || data.projectId;
      const numPages = data.data?.numPages || data.numPages;
      const autoSaved = data.data?.autoSaved || data.autoSaved;

      if (!projectId) {
        log('❌ No project ID in save event');
        return;
      }

      log('📊 Save event data:', { projectId, numPages, autoSaved });

      const currentVariantData = getCurrentVariantData();

      // Extract complete metadata
      const eventNumPages = data.data?.numPages || data.numPages || (currentVariantData.options[0] === 'Single Sided' ? 1 : 2);
      const shape = currentVariantData.options[1] || null;
      const sides = currentVariantData.options[0] || null;
      const size = currentVariantData.options[2] || null;

      // NEW: Infer sides from numPages if available
      if (numPages) {
        const inferredSides = numPages === 1 ? 'Single Sided' : numPages === 2 ? 'Double Sided' : null;
        log('🔍 Inferred from numPages:', { numPages, inferredSides });
      }

      // Prepare payload for cloud API
      const payload = {
        projectId: projectId,
        customerId: window.ppCustomerData?.id || 'guest',
        productId: window.ppProduct?.id || getProductId(),
        variantId: currentVariantData.id,
        options: currentVariantData.options,
        numPages: eventNumPages,
        shape: shape,
        sides: sides,
        size: size,
        designId: data.designId || null,
        designTitle: data.designTitle || null,
        metadata: {
          autoSaved: autoSaved || false,
          source: 'project-saved-event',
          userAgent: navigator.userAgent,
          timestamp: Date.now()
        }
      };

      // 🆕 NEW: Call cloud persistence API
      try {
        const response = await fetch('https://fz8y24ewil.execute-api.us-east-1.amazonaws.com/staging/api/projects', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shop-Domain': window.Shopify?.shop || 'studio.popsigns.co'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const result = await response.json();
          log('✅ Project data persisted to cloud:', result.projectId);
        } else {
          throw new Error(`API returned ${response.status}`);
        }
      } catch (apiError) {
        log('⚠️ Cloud persistence failed, using localStorage fallback:', apiError);
        // Continue with localStorage fallback
      }

      // NEW: Store in pending queue (for auto-save timing issue)
      // This ensures we don't lose variant data if PitchPrint hasn't written to localStorage yet
      const pendingKey = `pp_variant_pending_${projectId}`;
      const pendingData = {
        variantId: currentVariantData.id,
        options: currentVariantData.options,
        numPages: numPages,
        timestamp: Date.now(),
        autoSaved: autoSaved
      };

      try {
        localStorage.setItem(pendingKey, JSON.stringify(pendingData));
        log('📝 Stored in pending queue:', pendingData);
      } catch (e) {
        log('⚠️ Failed to store pending data:', e);
      }

      // Save variant state with project association (legacy localStorage)
      saveVariantState(currentVariantData.id, currentVariantData.options, projectId);
      log('💾 Variant state saved with project:', { projectId, variant: currentVariantData });

      // Lock controls
      lockControls();

      // Notify StateManager with COMPLETE project metadata
      if (stateManager) {
        stateManager.setProject({
          projectId: projectId,
          variant: currentVariantData,
          numPages: eventNumPages,
          shape: shape,
          sides: sides,
          size: size
        });

        log('📡 StateManager notified with complete project data (project-saved):', {
          projectId,
          variantId: currentVariantData.id,
          numPages: eventNumPages,
          shape,
          sides
        });
      }

    } catch (error) {
      log('❌ Error handling project saved:', error);
    }
  };

  const handleProjectCleared = () => {
    log('🧹 Project cleared - unlocking variants');
    
    try {
      clearSavedVariantState();
      unlockControls();
      
      if (stateManager) {
        stateManager.clearProject();
      }
    } catch (error) {
      log('❌ Error handling project cleared:', error);
    }
  };

  // =====================================
  // UTILITY FUNCTIONS - All Safe
  // =====================================

  /**
   * Check if current variant options indicate a hardware-only variant
   * and update the StateManager accordingly
   */
  const checkAndUpdateHardwareOnlyState = () => {
    if (!stateManager) {
      log('⚠️ StateManager not available for hardware-only check');
      return;
    }

    try {
      const currentVariant = getCurrentVariantData();
      const isHardwareOnly = stateManager.checkVariantOptions(currentVariant.options);

      log('🔧 Hardware-only check:', {
        options: currentVariant.options,
        isHardwareOnly: isHardwareOnly
      });

      // Update state (this will emit event if changed)
      stateManager.setHardwareOnly(isHardwareOnly);

      // Also toggle visibility of customization sections
      toggleCustomizationSections(!isHardwareOnly);

    } catch (error) {
      log('❌ Error checking hardware-only state:', error);
    }
  };

  /**
   * Show or hide the customization sections (PitchPrint, file upload, design toggle, confidence builder)
   * and update step numbers accordingly
   * @param {boolean} show - Whether to show the sections
   */
  const toggleCustomizationSections = (show) => {
    // PitchPrint section - try multiple selectors
    const pitchprintSection = document.querySelector('.pitchprint-section') ||
                              document.querySelector('.pitchprint-button-section') ||
                              document.querySelector('#pitchprint-section');

    // File upload section
    const fileUploadSection = document.querySelector('.custom-file-uploader') ||
                              document.querySelector('#file-upload-section') ||
                              document.querySelector('.file-upload-section');

    // Design method toggle
    const designToggle = document.querySelector('.design-method-toggle');

    // Confidence builder section (design-related marketing content)
    const confidenceBuilder = document.querySelector('.step2-confidence-builder');

    // Design thumbnails / collection preview section
    const designThumbnails = document.querySelector('.step2-collection-preview');

    // When showing sections, respect the current design method to avoid
    // re-showing sections that DesignMethodToggle already hid (race condition
    // when user clicks File Upload while variant change is still processing)
    const designMethod = show && stateManager ? stateManager.getDesignMethod() : null;

    log('🎛️ Toggling customization sections:', { show, designMethod });

    if (pitchprintSection) {
      const showPP = show && designMethod !== 'file_upload';
      pitchprintSection.style.display = showPP ? '' : 'none';
      log(`  PitchPrint section (.pitchprint-section): ${showPP ? 'visible' : 'hidden'}`);
    }

    if (fileUploadSection) {
      const showFU = show && designMethod !== 'pitchprint';
      fileUploadSection.style.display = showFU ? '' : 'none';
      log(`  File upload section: ${showFU ? 'visible' : 'hidden'}`);
    }

    if (designToggle) {
      designToggle.style.display = show ? '' : 'none';
      log(`  Design toggle: ${show ? 'visible' : 'hidden'}`);
    }

    if (confidenceBuilder) {
      const showCB = show && designMethod !== 'file_upload';
      confidenceBuilder.style.display = showCB ? '' : 'none';
      log(`  Confidence builder: ${showCB ? 'visible' : 'hidden'}`);
    }

    if (designThumbnails) {
      const showDT = show && designMethod !== 'file_upload';
      designThumbnails.style.display = showDT ? '' : 'none';
      log(`  Design thumbnails: ${showDT ? 'visible' : 'hidden'}`);
    }

    // Update step numbers based on visible sections
    updateStepNumbers(!show);
  };

  /**
   * Update step numbers when customization sections are hidden/shown
   * @param {boolean} customizationHidden - Whether customization sections are hidden
   */
  const updateStepNumbers = (customizationHidden) => {
    log('📊 Updating step numbers (customization hidden:', customizationHidden, ')');

    // Get all step badges on the page
    const allStepBadges = document.querySelectorAll('.step-badge');
    let currentStep = 1;

    allStepBadges.forEach((badge) => {
      // Find the parent section container
      const parentSection = badge.closest(
        '.custom-tab-body, ' +
        '.design-method-toggle, ' +
        '.pitchprint-section, ' +
        '.custom-file-uploader, ' +
        '.file-upload-section, ' +
        '.product-form__buttons, ' +
        '.celebration-header, ' +
        '.step-badge-container, ' +
        '.quantity-step-wrapper'
      );

      // Check if this section or any ancestor is hidden
      let isHidden = false;
      let element = parentSection || badge.parentElement;

      while (element && element !== document.body) {
        if (element.style.display === 'none') {
          isHidden = true;
          break;
        }
        element = element.parentElement;
      }

      if (isHidden) {
        log(`  Skipping hidden step badge in: ${parentSection?.className || 'unknown'}`);
        return;
      }

      // Update visible step badge
      badge.textContent = currentStep;
      log(`  Step ${currentStep}: ${parentSection?.className || badge.parentElement?.className || 'unknown'}`);
      currentStep++;
    });

    log(`📊 Step numbers updated - total visible steps: ${currentStep - 1}`);
  };

  const getCurrentVariantData = () => {
    try {
      const variantId = document.querySelector('input[name="id"]')?.value;
      const options = Array.from(document.querySelectorAll('select[name^="options"]'))
        .map(select => select.value)
        .filter(value => value && value !== '');
      
      return { id: variantId, options };
    } catch (error) {
      log('❌ Error getting current variant data:', error);
      return { id: null, options: [] };
    }
  };

  const getActiveProjectId = () => {
    try {
      // DEBUG: Inspect localStorage structure
      log('🔍 INSPECTING LOCALSTORAGE FOR PROJECT:');
      log('  pprint-projects:', localStorage.getItem('pprint-projects')?.substring(0, 300));
      log('  StateManager projectId:', stateManager?.getProjectId());
      log('  ppclient._vars.projectId:', window.ppclient?._vars?.projectId);

      // Check StateManager first
      if (stateManager && stateManager.getProjectId()) {
        log('✅ Found projectId in StateManager');
        return stateManager.getProjectId();
      }

      // Check PitchPrint client
      if (window.ppclient?._vars?.projectId) {
        log('✅ Found projectId in ppclient._vars');
        return window.ppclient._vars.projectId;
      }

      log('❌ No projectId found in StateManager or ppclient');
      return null;
    } catch (error) {
      log('❌ Error getting active project ID:', error);
      return null;
    }
  };

  // NEW: Get project data with variant information from PitchPrint localStorage
  const getProjectDataFromPitchPrintStorage = (projectId) => {
    try {
      if (!projectId) return null;

      log('🔍 Looking for project data in PitchPrint localStorage:', projectId);

      // Check all three PitchPrint localStorage keys
      const pprintProjects = localStorage.getItem('pprint-projects');
      const pprintSp = localStorage.getItem('pprint-sp');

      if (pprintProjects) {
        const projects = JSON.parse(pprintProjects);
        const encodedData = projects[projectId];

        if (encodedData) {
          try {
            // Decode URL-encoded JSON
            const projectData = JSON.parse(decodeURIComponent(encodedData));
            log('✅ Found project data in pprint-projects:', projectData);

            // Return variant data if present
            if (projectData.shopifyVariant) {
              log('✅ Found shopifyVariant in project data:', projectData.shopifyVariant);
              return projectData.shopifyVariant;
            }
          } catch (e) {
            log('⚠️ Error decoding project data:', e);
          }
        }
      }

      // Fallback: check pprint-sp by product ID
      if (pprintSp) {
        const productId = getProductId();
        if (productId) {
          const projects = JSON.parse(pprintSp);
          const encodedData = projects[productId];

          if (encodedData) {
            try {
              // Handle both URL-encoded strings and already-parsed objects
              let projectData;
              if (typeof encodedData === 'string') {
                projectData = JSON.parse(decodeURIComponent(encodedData));
              } else if (typeof encodedData === 'object') {
                projectData = encodedData;
              } else {
                log('⚠️ Unexpected data type in pprint-sp:', typeof encodedData);
                return null;
              }

              if (projectData.projectId === projectId && projectData.shopifyVariant) {
                log('✅ Found shopifyVariant in pprint-sp:', projectData.shopifyVariant);
                return projectData.shopifyVariant;
              }
            } catch (e) {
              log('⚠️ Error decoding pprint-sp data:', e);
            }
          }
        }
      }

      log('ℹ️ No variant data found in PitchPrint localStorage');
      return null;
    } catch (error) {
      log('❌ Error getting project data from PitchPrint storage:', error);
      return null;
    }
  };

  const saveVariantState = (variantId, options, projectId) => {
    if (!projectId || !variantId) return;

    try {
      const productId = getProductId();
      if (!productId) return;

      // Save to legacy localStorage (backwards compatibility)
      const storageKey = `pp_variant_${productId}`;
      const variantState = {
        variantId,
        options,
        projectId,
        timestamp: Date.now()
      };

      localStorage.setItem(storageKey, JSON.stringify(variantState));
      log('💾 Variant state saved to localStorage:', variantState);

      // NEW: Save to PitchPrint storage with retry logic for auto-save timing
      saveVariantToPitchPrintStorageWithRetry(variantId, options, projectId, productId);
    } catch (e) {
      log('❌ Failed to save variant state:', e);
    }
  };

  // NEW: Retry wrapper for auto-save timing issue
  const saveVariantToPitchPrintStorageWithRetry = (variantId, options, projectId, productId, attemptNumber = 1) => {
    const maxAttempts = 4;
    const delays = [0, 500, 1500, 3500]; // Cumulative delays: 0ms, 500ms, 2s, 5s

    log(`🔄 Attempt ${attemptNumber}/${maxAttempts} to save variant to PitchPrint storage`);

    const success = saveVariantToPitchPrintStorage(variantId, options, projectId, productId);

    if (!success && attemptNumber < maxAttempts) {
      const nextDelay = delays[attemptNumber] - delays[attemptNumber - 1];
      log(`⏳ Retry scheduled in ${nextDelay}ms (attempt ${attemptNumber + 1}/${maxAttempts})`);

      setTimeout(() => {
        saveVariantToPitchPrintStorageWithRetry(variantId, options, projectId, productId, attemptNumber + 1);
      }, nextDelay);
    } else if (!success && attemptNumber >= maxAttempts) {
      log('❌ All retry attempts exhausted - variant not saved to PitchPrint storage');
      log('💡 Variant is saved in legacy localStorage and pending queue');
    }
  };

  // NEW: Save variant data to PitchPrint's localStorage (cross-device persistence)
  // Returns true if successful, false if project not found (needs retry)
  const saveVariantToPitchPrintStorage = (variantId, options, projectId, productId) => {
    try {
      log('💾 Saving variant to PitchPrint storage:', { variantId, options, projectId });

      // Get existing PitchPrint localStorage
      const pprintProjects = localStorage.getItem('pprint-projects');
      const pprintSp = localStorage.getItem('pprint-sp');
      const ppW2pProjects = localStorage.getItem('pp_w2p_projects');

      if (!pprintProjects && !pprintSp) {
        log('⚠️ No PitchPrint localStorage found - will retry');
        return false;
      }

      // Check pending queue for this project (in case we're retrying)
      const pendingKey = `pp_variant_pending_${projectId}`;
      const pendingData = localStorage.getItem(pendingKey);
      if (pendingData) {
        try {
          const pending = JSON.parse(pendingData);
          log('📥 Found pending data for project:', pending);
          // Use pending data if provided values are missing
          if (!variantId && pending.variantId) variantId = pending.variantId;
          if (!options && pending.options) options = pending.options;
        } catch (e) {
          log('⚠️ Error parsing pending data:', e);
        }
      }

      // Parse existing data
      let projects = pprintProjects ? JSON.parse(pprintProjects) : {};
      let sp = pprintSp ? JSON.parse(pprintSp) : {};
      let w2p = ppW2pProjects ? JSON.parse(ppW2pProjects) : {};

      let updated = false;

      // Update pprint-projects (keyed by projectId)
      if (projects[projectId]) {
        try {
          const projectData = JSON.parse(decodeURIComponent(projects[projectId]));
          projectData.shopifyVariant = { variantId, options };
          projects[projectId] = encodeURIComponent(JSON.stringify(projectData));
          updated = true;
          log('✅ Updated pprint-projects with variant data');
        } catch (e) {
          log('⚠️ Error updating pprint-projects:', e);
        }
      }

      // Update pprint-sp (keyed by productId)
      if (sp[productId]) {
        try {
          const projectData = JSON.parse(decodeURIComponent(sp[productId]));
          if (projectData.projectId === projectId) {
            projectData.shopifyVariant = { variantId, options };
            sp[productId] = encodeURIComponent(JSON.stringify(projectData));
            updated = true;
            log('✅ Updated pprint-sp with variant data');
          }
        } catch (e) {
          log('⚠️ Error updating pprint-sp:', e);
        }
      }

      // Update pp_w2p_projects (keyed by productId)
      if (w2p[productId]) {
        try {
          const projectData = JSON.parse(decodeURIComponent(w2p[productId]));
          if (projectData.projectId === projectId) {
            projectData.shopifyVariant = { variantId, options };
            w2p[productId] = encodeURIComponent(JSON.stringify(projectData));
            updated = true;
            log('✅ Updated pp_w2p_projects with variant data');
          }
        } catch (e) {
          log('⚠️ Error updating pp_w2p_projects:', e);
        }
      }

      // Save back to localStorage if any updates were made
      if (updated) {
        if (Object.keys(projects).length > 0) {
          localStorage.setItem('pprint-projects', JSON.stringify(projects));
        }
        if (Object.keys(sp).length > 0) {
          localStorage.setItem('pprint-sp', JSON.stringify(sp));
        }
        if (Object.keys(w2p).length > 0) {
          localStorage.setItem('pp_w2p_projects', JSON.stringify(w2p));
        }
        log('✅ Variant data persisted to PitchPrint storage for cross-device access');

        // Clean up pending queue on success
        const pendingKey = `pp_variant_pending_${projectId}`;
        try {
          localStorage.removeItem(pendingKey);
          log('🧹 Cleaned up pending queue');
        } catch (e) {
          log('⚠️ Error cleaning pending queue:', e);
        }

        return true; // Success
      } else {
        log('⚠️ Project not found in PitchPrint storage - needs retry');
        return false; // Not found, needs retry
      }
    } catch (error) {
      log('❌ Error saving variant to PitchPrint storage:', error);
      return false; // Error, needs retry
    }
  };

  const getSavedVariantData = () => {
    try {
      const productId = getProductId();
      if (!productId) return null;

      const storageKey = `pp_variant_${productId}`;
      const saved = localStorage.getItem(storageKey);
      
      if (saved) {
        try {
          const data = JSON.parse(saved);
          
          // Only return if saved within last 24 hours AND has project ID
          if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000 && data.projectId) {
            return data;
          } else {
            clearSavedVariantState();
            return null;
          }
        } catch (e) {
          clearSavedVariantState();
          return null;
        }
      }
      
      return null;
    } catch (error) {
      log('❌ Error getting saved variant data:', error);
      return null;
    }
  };

  const clearSavedVariantState = () => {
    try {
      const productId = getProductId();
      if (productId) {
        const storageKey = `pp_variant_${productId}`;
        localStorage.removeItem(storageKey);
        log('🗑️ Saved variant state cleared');
      }
    } catch (error) {
      log('❌ Error clearing variant state:', error);
    }
  };

  const getProductId = () => {
    try {
      return document.querySelector('[data-product-id]')?.dataset.productId ||
             document.querySelector('input[name="product-id"]')?.value ||
             window.location.pathname.split('/').pop().split('?')[0];
    } catch (error) {
      log('❌ Error getting product ID:', error);
      return null;
    }
  };

  const lockControls = () => {
    log('🔒 Locking variant controls');
    try {
      document.querySelectorAll('.card-p-style').forEach(el => {
        el.style.pointerEvents = 'none';
        el.style.opacity = '0.6';
        el.style.filter = 'grayscale(50%)';
      });
      document.querySelectorAll('select[name^="options"]').forEach(el => {
        el.disabled = true;
      });
    } catch (error) {
      log('❌ Error locking controls:', error);
    }
  };

  const unlockControls = () => {
    log('🔓 Unlocking variant controls');
    try {
      document.querySelectorAll('.card-p-style').forEach(el => {
        el.style.pointerEvents = '';
        el.style.opacity = '';
        el.style.filter = '';
      });
      document.querySelectorAll('select[name^="options"]').forEach(el => {
        el.disabled = false;
      });
    } catch (error) {
      log('❌ Error unlocking controls:', error);
    }
  };

  const ensurePitchPrintActiveState = (projectId) => {
    log('🔧 Ensuring PitchPrint is in active editing state for project:', projectId);
    
    try {
      if (!window.ppclient) {
        log('⚠️ PitchPrint client not available yet');
        return;
      }

      // Set PitchPrint project ID
      if (window.ppclient._vars) {
        window.ppclient._vars.projectId = projectId;
        log('✅ Set PitchPrint project ID:', projectId);
      }

      // Trigger restoration complete event
      setTimeout(() => {
        if (window.ppclient.fire) {
          const projectSavedData = {
            projectId: projectId,
            type: 'project-saved',
            source: 'restoration'
          };
          
          window.ppclient.fire('project-restoration-complete', projectSavedData);
          log('✅ Triggered PitchPrint active state setup');
        }
      }, 200);

      // Ensure UI elements are visible
      setTimeout(() => {
        const editButton = document.querySelector('#pp_edit_btn');
        const clearButton = document.querySelector('#pp_clear_design_btn');
        
        if (editButton) {
          editButton.style.display = 'inline-block';
          log('✅ Edit button activated');
        }
        
        if (clearButton) {
          clearButton.style.display = 'inline-block';
          log('✅ Clear button activated');
        }
      }, 300);

    } catch (error) {
      log('❌ Error ensuring PitchPrint active state:', error);
    }
        
        // At the very end, before the closing }, add:
      setTimeout(() => {
        forceGalleryPreviewEnable(projectId);
      }, 500);
    };

  const updatePitchPrintObservables = () => {
    try {
      if (!window.ppclient?.fire) return;
      
      const sidesEl = document.querySelector('#option1');
      const shapeEl = document.querySelector('#option2');
      
      const sidesVal = sidesEl?.value;
      const shapeVal = shapeEl?.value;

      if (sidesVal) {
        window.ppclient.fire('set-single-double-sided', sidesVal);
      }

      window.ppclient.fire('set-design-observables', {
        sidesSelector: '#option1',
        shapeSelector: '#option2',
      });
      
      log('✅ PitchPrint observables updated');
    } catch (error) {
      log('❌ Error updating PitchPrint observables:', error);
    }
  };

  // =====================================
  // CONDITIONAL VARIANTS - Show/Hide Logic
  // =====================================

  const applyConditionalVariants = () => {
    const container = document.querySelector('.custom-tab-body');
    if (!container) return;

    const conditionalEnabled = container.dataset.conditionalEnabled === 'true';
    if (!conditionalEnabled) {
      log('ℹ️ Conditional variants not enabled for this product');
      return;
    }

    try {
      const rulesJson = container.dataset.conditionalRules;
      if (!rulesJson) {
        log('⚠️ No conditional rules found');
        return;
      }

      // Parse the rules
      const rules = JSON.parse(rulesJson);
      log('📋 Conditional variant rules loaded:', rules);

      // Get current selections
      const getCurrentSelections = () => {
        const selections = {};
        document.querySelectorAll('select[name^="options"]').forEach((select, idx) => {
          const optionName = select.name.match(/\[(.*?)\]/)?.[1];
          if (optionName && select.value) {
            selections[optionName] = select.value;
            selections[`option${idx + 1}`] = select.value; // Also store by index
          }
        });
        return selections;
      };

      // Apply visibility rules
      const applyVisibilityRules = () => {
        const selections = getCurrentSelections();
        log('🔍 Current selections:', selections);

        // Reset all cards to visible first
        document.querySelectorAll('.card-p-style').forEach(card => {
          card.classList.remove('conditionally-hidden');
        });

        // Reset all hidden option groups
        document.querySelectorAll('.conditionally-hidden-group').forEach(group => {
          group.classList.remove('conditionally-hidden-group');
          group.style.display = '';
        });

        // Apply each rule
        rules.forEach((rule, ruleIndex) => {
          try {
            // Check if the condition is met
            let conditionMet = false;

            if (rule.if_option && rule.if_value) {
              const selectedValue = selections[rule.if_option];
              conditionMet = selectedValue === rule.if_value;
              log(`Rule ${ruleIndex + 1}: ${rule.if_option} = "${selectedValue}" ${conditionMet ? '===' : '!=='} "${rule.if_value}"`);
            }

            // If condition is met, apply the hide action
            if (conditionMet && rule.then_hide_option) {
              // Check if we should hide the entire option group
              if (rule.hide_entire_option === true) {
                // Find the option group by looking for the .option-group with this data-option-name
                const escapedOptionName = CSS.escape(rule.then_hide_option);
                const optionGroup = document.querySelector(`.option-group[data-option-name="${escapedOptionName}"]`);
                if (optionGroup) {
                  optionGroup.classList.add('conditionally-hidden-group');
                  optionGroup.style.display = 'none';
                  log(`🙈 Hiding entire option group: ${rule.then_hide_option}`);
                }
              }
              // Otherwise hide specific values
              else if (rule.hide_values) {
                const hideValues = Array.isArray(rule.hide_values) ? rule.hide_values : [rule.hide_values];

                hideValues.forEach(valueToHide => {
                  // Find all cards for this option with this value
                  // Use CSS.escape() to safely escape special characters in attribute values
                  const escapedOptionName = CSS.escape(rule.then_hide_option);
                  const escapedValue = CSS.escape(valueToHide);
                  const cardsToHide = document.querySelectorAll(
                    `.card-p-style[data-option-name="${escapedOptionName}"][data-value="${escapedValue}"]`
                  );

                  cardsToHide.forEach(card => {
                    card.classList.add('conditionally-hidden');
                    log(`🙈 Hiding: ${rule.then_hide_option} = "${valueToHide}"`);
                  });
                });
              }
            }
          } catch (ruleError) {
            log(`❌ Error applying rule ${ruleIndex + 1}:`, ruleError);
          }
        });

        // If the currently active option was hidden, select the first visible one
        document.querySelectorAll('.option-group').forEach(group => {
          const activeCard = group.querySelector('.card-p-style.active');
          if (activeCard && activeCard.classList.contains('conditionally-hidden')) {
            log('⚠️ Active option was hidden, selecting first visible option');

            const firstVisible = group.querySelector('.card-p-style:not(.conditionally-hidden)');
            if (firstVisible) {
              // Programmatically click the first visible option
              firstVisible.click();
            }
          }
        });
      };

      // Apply rules on page load
      applyVisibilityRules();

      // Watch for variant changes
      document.querySelectorAll('select[name^="options"]').forEach(select => {
        select.addEventListener('change', () => {
          log('🔄 Option changed, reapplying conditional rules');
          applyVisibilityRules();
        });
      });

      log('✅ Conditional variants system initialized');
    } catch (error) {
      log('❌ Error setting up conditional variants:', error);
    }
  };

  // =====================================
  // INITIALIZATION - Safe & Robust
  // =====================================

  const init = () => {
    if (initialized) {
      log('Already initialized, skipping...');
      return;
    }
    initialized = true;
    log('🚀 Initializing streamlined card variants system...');

    try {
      setupVariantControls();
      setupStateManagerListeners();
      setupPitchPrintIntegration();
      applyConditionalVariants(); // NEW: Initialize conditional variants

      log('✅ Streamlined card variants system ready');
    } catch (error) {
      log('❌ Error during initialization:', error);
    }
  };

  const setupStateManagerListeners = () => {
    if (!stateManager) return;
    
    try {
      stateManager.on('project:restored', (event) => {
        const { projectId, variant } = event.detail;
        log('🔄 Project restored via StateManager:', { projectId, variant });
        
        if (variant && variant.id) {
          applyVariant(variant.id, variant.options);
        }
        lockControls();
      });

      stateManager.on('project:cleared', () => {
        log('🧹 Project cleared via StateManager');
        handleProjectCleared();
      });

      log('📡 StateManager listeners attached');
    } catch (error) {
      log('❌ Error setting up StateManager listeners:', error);
    }
  };

  const forceGalleryPreviewEnable = (projectId) => {
  log('🖼️ Forcing gallery preview enable for project:', projectId);

  // Track if preview has loaded successfully
  let previewLoaded = false;

  // Multiple refresh attempts: try native first, then fallback to manual injection
  const refreshAttempts = [
    { delay: 200, useManualFallback: false },   // Native
    { delay: 500, useManualFallback: false },   // Native
    { delay: 1000, useManualFallback: true },   // Manual fallback
    { delay: 2000, useManualFallback: true }    // Manual fallback
  ];

  refreshAttempts.forEach((attempt, index) => {
    setTimeout(() => {
      // Skip if preview already loaded
      if (previewLoaded) {
        log(`⏭️ Skipping attempt ${index + 1} - preview already loaded`);
        return;
      }

      const strategy = attempt.useManualFallback ? 'manual fallback' : 'native';
      log(`🔄 Gallery refresh attempt ${index + 1}/${refreshAttempts.length} (delay: ${attempt.delay}ms, strategy: ${strategy})`);

      // Method 1: Call refresh function with appropriate strategy
      if (window.refreshGalleryPreview) {
        window.refreshGalleryPreview(attempt.useManualFallback);
        log(`✅ Called refreshGalleryPreview (${strategy})`);
      } else if (window.enableGalleryPreview) {
        window.enableGalleryPreview(true, attempt.useManualFallback);
        log(`✅ Called enableGalleryPreview (${strategy})`);
      }

      // Method 2: Fire PitchPrint events (only for native attempts)
      if (!attempt.useManualFallback && window.ppclient?.fire) {
        window.ppclient.fire('enable-gallery-preview', { projectId });
        window.ppclient.fire('project-gallery-restore', { projectId });
        window.ppclient.fire('render-preview');
        log('🔥 Fired gallery events (native)');
      }

      // Method 3: Show hidden gallery elements
      const selectors = ['.pitchprint-gallery-preview', '.gallery-preview', '.pitchprint-preview-image'];
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          if (el.style.display === 'none') {
            el.style.display = 'block';
            log('🖼️ Showed gallery element:', selector);
          }
        });
      });

      // Check if preview loaded successfully after this attempt
      setTimeout(() => {
        const previewImages = document.querySelectorAll('.pitchprint-preview-image');
        const visiblePreviews = Array.from(previewImages).filter(img => {
          return img.offsetParent !== null && img.src && img.src.length > 0;
        });

        if (visiblePreviews.length > 0) {
          previewLoaded = true;
          log(`✅ Preview loaded successfully on attempt ${index + 1} - skipping remaining retries`);
        }
      }, 100);
    }, attempt.delay);
  });
};


  const setupPitchPrintIntegration = () => {
    // Check if this is a PitchPrint product using window.productConfig
    const isPitchPrintProduct = window.productConfig?.usesPitchprint === true;

    if (!isPitchPrintProduct) {
      // For non-PitchPrint products, check URL variant first, then fall back to popular variants
      log('ℹ️ Non-PitchPrint product - skipping PitchPrint integration');
      setTimeout(() => {
        if (userHasInteracted) {
          log('👤 User already changed variant — skipping auto-selection');
        } else {
          // Check if URL has a variant parameter - if so, respect it
          const urlVariantApplied = applyUrlVariantIfPresent();

          if (!urlVariantApplied) {
            // No URL variant, apply popular variants as default
            applyPopularVariants();
          }
        }

        unlockControls();
        log('✅ Standard product variant controls ready');
      }, 100);
      return;
    }

    // PitchPrint product - wait for PitchPrint client
    let attempts = 0;
    const maxAttempts = 50;

    const watcher = setInterval(() => {
      attempts++;

      try {
        if (window.ppclient && window.ppclient.on) {
          clearInterval(watcher);
          log('🎨 PitchPrint client detected');

          window.ppclient.on('project-saved', handleProjectSaved);
          log('✅ PitchPrint project-saved handler attached');

          // Initial project check
          setTimeout(() => {
            handleProjectRestoration();
          }, 500);

          if (stateManager) {
            stateManager.setSystemReady();
          }
          return;
        }

        if (attempts % 10 === 0) {
          log(`⏳ Waiting for PitchPrint... (${attempts}/${maxAttempts})`);
        }

        if (attempts >= maxAttempts) {
          clearInterval(watcher);
          log('⚠️ PitchPrint timeout - proceeding without integration');

          setTimeout(() => {
            handleProjectRestoration();
          }, 500);

          if (stateManager) {
            stateManager.setSystemReady();
          }
        }
      } catch (error) {
        log('❌ Error in PitchPrint integration setup:', error);
        if (attempts >= maxAttempts) {
          clearInterval(watcher);
        }
      }
    }, 200);
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }

  log('📝 Streamlined card variants script loaded');
})();